/**
 * Settings → Security (2026-06-16 overhaul).
 *
 * Two REAL controls, zero fake switches:
 *
 *   1. Password requirements — read-only. The password policy is enforced
 *      globally by the identity service (svdy-authorization
 *      `validate_complex_password`: ≥8 chars + letter + number + special)
 *      at sign-up, password change, and reset. Shown here as honest
 *      information, not an editable per-org slider that wouldn't enforce.
 *
 *   2. Require 2FA for administrators — a real org-level toggle stored in
 *      settings.security and enforced by `Enforce2FAGate` (mounted in
 *      MainLayout): a privileged user who hasn't enrolled 2FA is blocked
 *      until they do. Frontline workers stay optional.
 *
 * Removed in the overhaul: the old editable min-length / complexity /
 * expiry / session-timeout / max-sessions fields (none were enforced —
 * fake compliance) and the 2FA "coming soon" card (now real).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Typography, Button, Grid, Card, CardContent, Chip, Divider,
  Stack, Switch, CircularProgress, Snackbar, List, ListItem,
  ListItemIcon, ListItemText,
} from '@mui/material';
import { Save, CheckCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiError, systemApi } from '../../../api';
import { twoFactorApi } from '../../../api/twofa';
import { useRoleLanding } from '../../../hooks/useRoleLanding';
import { translateApiError } from '../../../i18n/translateApiError';

interface SecurityData {
  require_2fa_for_admins?: boolean;
  [k: string]: unknown;
}

const Security: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<SecurityData>({ require_2fa_for_admins: false });
  const navigate = useNavigate();
  const { securityPath } = useRoleLanding();
  // The signed-in admin's OWN enrolment. `null` while loading; a failed read
  // stays null so the row shows a spinner rather than asserting "Off", which
  // would be a wrong statement about someone's security posture.
  const [my2fa, setMy2fa] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    twoFactorApi.status()
      .then((s) => { if (alive) setMy2fa(!!s.enrolled); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemApi.systemSettingsApi.getSecurity();
      if (res.data && Object.keys(res.data).length > 0) {
        setData((d) => ({ ...d, ...(res.data as SecurityData) }));
      }
    } catch { /* defaults are fine */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    setSaving(true);
    try {
      // Persist only the real, enforced field. Other keys (legacy fake
      // sliders) are intentionally not sent.
      await systemApi.systemSettingsApi.updateSecurity({
        require_2fa_for_admins: !!data.require_2fa_for_admins,
      });
      setToast(t('system:shared.savedToast', { module: 'Security' }));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:shared.failedToSave'));
    } finally { setSaving(false); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  // The four the identity service actually enforces
  // (svdy-authorization `validate_complex_password`). `reqSpecial` used to name a
  // fixed list "(@$!%*#?&)" — that was the OLD character whitelist, and after it
  // was removed the list became actively misleading: it told an admin their staff
  // could not use a hyphen.
  const requirements = [
    t('system:security.reqMinLength'),
    t('system:security.reqLetter'),
    t('system:security.reqNumber'),
    t('system:security.reqSpecial'),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('system:security.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('system:security.subtitle')}</Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          onClick={save}
          disabled={saving}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
        >
          {saving ? t('system:shared.saving') : t('system:shared.saveChanges')}
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ alignSelf: 'stretch' }}>
        {/* Password requirements — read-only, enforced by the identity service. */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Box sx={{ px: 2.5, py: 1.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('system:security.passwordCardTitle')}</Typography>
            </Box>
            <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.82rem' }}>
                {t('system:security.passwordIntro')}
              </Typography>
              <List dense disablePadding>
                {requirements.map((r) => (
                  <ListItem key={r} disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleOutline sx={{ fontSize: 18, color: 'success.main' }} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem' } }} primary={r} />
                  </ListItem>
                ))}
              </List>
              {/* Why the card is read-only, stated instead of left to be
                  inferred. The policy is a module constant in
                  svdy-authorization (`validate_complex_password`) shared by every
                  organization, not a per-org setting — an admin who is not told
                  that reads a greyed-out list as a bug or as a missing feature.
                  The 2026-06-16 overhaul deliberately removed the editable
                  min-length / complexity / expiry fields because none of them
                  were enforced; showing the real rule and saying who owns it is
                  the honest version of that. */}
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: 'block', mt: 1, fontSize: '0.72rem' }}
              >
                {t('system:security.passwordSetBy')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Two-factor authentication — real per-org toggle, enforced by Enforce2FAGate. */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Box sx={{ px: 2.5, py: 1.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('system:security.twoFactorCardTitle')}</Typography>
            </Box>
            <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
              {/* Label left, control right — the same shape as the row below the
                  divider. It was a FormControlLabel with a two-line label, which puts
                  the switch at the card's content edge and pushes the title AND the
                  description to the switch's right edge. That gave one short card
                  three different left origins: the card title and the bottom row at
                  the content edge, the title and description ~60px in (owner report,
                  screenshot 2026-08-04). svdy-ui §15 — count the horizontal origins;
                  the answer must be one. */}
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={1.5}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {t('system:security.require2faAdmins')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.72rem', display: 'block', mt: 0.25 }}
                  >
                    {t('system:security.require2faAdminsDesc')}
                  </Typography>
                </Box>
                <Switch
                  size="small"
                  checked={!!data.require_2fa_for_admins}
                  onChange={() => setData((d) => ({ ...d, require_2fa_for_admins: !d.require_2fa_for_admins }))}
                  sx={{ flexShrink: 0, mt: -0.25 }}
                />
              </Stack>

              {/* The admin's OWN 2FA state, and a link to set it up.
                  Two reasons this belongs here even though the credential is
                  personal and lives on a personal page:

                  1. Switching the toggle above ON without being enrolled locks
                     the person who switched it out of their own console at the
                     next sign-in. The page offered no hint of that.
                  2. It is the discoverability fix. Personal 2FA is a
                     low-frequency setting reached from the avatar menu; an admin
                     thinking about 2FA is looking at THIS page.

                  What it deliberately does NOT do is configure that credential
                  here. This page is org policy, shared by every admin under
                  `system.settings.manage` — a "set up 2FA" control on it would
                  raise "whose 2FA?", which is why Google Workspace, Okta and
                  Atlassian all keep enforcement policy and personal credential
                  on separate surfaces. */}
              <Divider sx={{ my: 2 }} />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1.5}
                flexWrap="wrap"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {t('system:security.yourOwn2fa')}
                  </Typography>
                  {my2fa === null ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Chip
                      label={
                        my2fa
                          ? t('system:security.yourOwn2faOn')
                          : t('system:security.yourOwn2faOff')
                      }
                      size="small"
                      variant="outlined"
                      color={my2fa ? 'success' : 'default'}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        borderRadius: 1,
                        ...(my2fa ? {} : { color: 'text.disabled' }),
                      }}
                    />
                  )}
                </Stack>
                {my2fa === false && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(securityPath)}
                    sx={{ fontSize: '0.8125rem' }}
                  >
                    {t('system:security.yourOwn2faSetUp')}
                  </Button>
                )}
              </Stack>

              {data.require_2fa_for_admins && my2fa === false && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 1 }}>
                  {t('system:security.require2faSelfWarning')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Security;
